import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from './useAuthStore';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /**
   * Rol requerido para acceder a la ruta
   * - 'superadmin': Solo usuarios con rol 'superadmin'
   * - 'admin': Solo usuarios con rol admin
   * - 'user': Solo usuarios (cualquier nivel)
   * - undefined: Cualquier usuario autenticado
   */
  requiredRole?: 'superadmin' | 'admin' | 'user';
}

function hasRequiredRole(
  user: { role: { name: string }; email: string },
  role: 'superadmin' | 'admin' | 'user'
): boolean {
  const isSuperAdmin = user.role.name === 'superadmin';
  if (role === 'superadmin') return isSuperAdmin;
  // superadmin tiene todos los permisos de admin
  if (role === 'admin') return user.role.name === 'admin' || isSuperAdmin;
  if (role === 'user') return ['user_high', 'user_medium', 'user_low'].includes(user.role.name);
  return true;
}

/**
 * Componente para proteger rutas que requieren autenticación y opcionalmente un rol específico
 *
 * @remarks
 * Redirige a /login si el usuario no está autenticado.
 * Redirige a /dashboard si el usuario no tiene el rol requerido.
 */
export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps): JSX.Element {
  const { isAuthenticated, isLoading, token, user } = useAuthStore();
  const location = useLocation();

  if (token && (isLoading || !user)) {
    return <></>;
  }

  if (!isAuthenticated || !user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  if (requiredRole && !hasRequiredRole(user, requiredRole)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
